import React, { useState } from "react";
import { ref, uploadBytes } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import { storage, functions } from "../firebase";
import Button from "./Button";

/**
 * Bottom-sheet modal that lets a memorial owner select up to 3 pinned photos.
 * Owners can also upload new photos directly from this sheet.
 */
export default function PinPhotosSheet({
  memorialId,
  availableOwnerImages,
  initialSelectedIds,
  onSave,
  onClose,
}) {
  const [selectedPinIds, setSelectedPinIds] = useState(initialSelectedIds);

  const togglePin = (imgId) => {
    setSelectedPinIds((prev) => {
      const cleaned = prev.filter(Boolean);
      const set = new Set(cleaned);
      if (set.has(imgId)) {
        set.delete(imgId);
      } else {
        if (set.size >= 3) return Array.from(set);
        set.add(imgId);
      }
      return Array.from(set);
    });
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const remaining = Math.max(0, 3 - new Set(selectedPinIds.filter(Boolean)).size);
    const call = httpsCallable(functions, "publishAdminImage");

    for (const file of files.slice(0, remaining)) {
      const tempPath = `memorials/${memorialId}/pending/__pin__/${Date.now()}_${file.name.replace(/\s/g, "_")}`;
      await uploadBytes(ref(storage, tempPath), file);
      const res = await call({ memorialId, pendingPath: tempPath });
      const memoryId = res?.data?.memoryId;
      if (memoryId) {
        setSelectedPinIds((prev) =>
          prev.length < 3 ? [...prev, memoryId] : prev
        );
      }
    }
    e.target.value = "";
  };

  const currentCount = new Set(selectedPinIds.filter(Boolean)).size;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-end justify-center"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white w-full rounded-t-[20px] max-h-[80vh] overflow-hidden">
        <div className="sticky top-0 z-10 bg-white">
          <div className="flex items-center justify-between p-4 border-b border-[rgba(0,0,0,0.06)]">
            <div className="text-[16px] font-semibold">Select up to 3 photos</div>
            <div className="flex items-center gap-2">
              <Button onClick={onClose}>Cancel</Button>
              <Button onClick={() => onSave(selectedPinIds)}>Save</Button>
            </div>
          </div>
        </div>

        <div className="p-4 overflow-y-auto" style={{ maxHeight: "calc(80vh - 60px)" }}>
          <div className="grid grid-cols-3 gap-3">
            <button
              onClick={() => document.getElementById("pin-upload-input")?.click()}
              className="relative rounded-[12px] overflow-hidden border border-dashed border-[#C8B8A6] flex items-center justify-center h-[100px] bg-[#FFF7EF]"
            >
              <span className="text-[#44231E] text-sm">Upload</span>
            </button>
            <input
              id="pin-upload-input"
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={handleUpload}
            />

            {availableOwnerImages.map((img) => {
              const idx = selectedPinIds.indexOf(img.id);
              const selected = idx !== -1;
              const disabled = !selected && currentCount >= 3;
              return (
                <button
                  key={img.id}
                  disabled={disabled}
                  onClick={() => togglePin(img.id)}
                  className={`relative rounded-[12px] overflow-hidden ${
                    selected ? "ring-2 ring-[#44231E]" : ""
                  } ${disabled ? "opacity-50" : ""}`}
                >
                  <img src={img.url} alt="" className="w-full h-[100px] object-cover" />
                  {selected && (
                    <div className="absolute top-2 left-2 w-6 h-6 rounded-full bg-white flex items-center justify-center text-[12px] font-semibold text-[#44231E]">
                      {idx + 1}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
